import React, { useState, useRef, useEffect } from "react";

const InputMask = ({
  id,
  label,
  mask,
  placeholder,
  value = "",
  onChange = () => {},
  className = "",
  disabled = false,
  required = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Mask characters mapping
  const maskMap = {
    9: /\d/, // digit 0-9
    a: /[a-zA-Z]/, // letter
    "*": /[a-zA-Z0-9]/, // alphanumeric
    "#": /[0-9]/, // number
    A: /[A-Z]/, // uppercase letter
    L: /[a-z]/, // lowercase letter
  };

  const applyMask = (inputVal, maskPattern) => {
    if (!maskPattern) return inputVal;

    let maskedValue = "";
    let inputIndex = 0;
    let maskIndex = 0;

    // Remove all non-alphanumeric characters from input for processing
    const cleanInput = inputVal.replace(/[^\w]/g, "");

    while (maskIndex < maskPattern.length && inputIndex < cleanInput.length) {
      const maskChar = maskPattern[maskIndex];
      const inputChar = cleanInput[inputIndex];

      if (maskMap[maskChar]) {
        // It's a mask character
        if (maskMap[maskChar].test(inputChar)) {
          maskedValue += inputChar;
          inputIndex++;
        } else {
          // Invalid character, skip it
          inputIndex++;
          continue;
        }
      } else {
        // It's a literal character in mask
        maskedValue += maskChar;
      }
      maskIndex++;
    }

    return maskedValue;
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const previousValue = inputValue;

    // Check if user is deleting (backspace/delete)
    const isDeleting = newValue.length < previousValue.length;

    if (isDeleting) {
      // For deletion, we need to handle it differently
      const maskedValue = applyMask(newValue, mask);
      setInputValue(maskedValue);
      onChange(maskedValue);

      // Set cursor position after deletion
      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = Math.min(cursorPosition, maskedValue.length);
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    } else {
      // For normal input
      const maskedValue = applyMask(newValue, mask);
      setInputValue(maskedValue);
      onChange(maskedValue);
    }
  };

  const handleKeyDown = (e) => {
    const cursorPosition = e.target.selectionStart;

    // Handle backspace specifically
    if (e.keyCode === 8) {
      // Backspace
      e.preventDefault();

      if (cursorPosition > 0) {
        const currentValue = inputValue;
        let newPosition = cursorPosition - 1;

        // Skip over literal characters when backspacing
        while (newPosition > 0 && mask && !maskMap[mask[newPosition]]) {
          newPosition--;
        }

        const newValue =
          currentValue.substring(0, newPosition) +
          currentValue.substring(cursorPosition);
        const maskedValue = applyMask(newValue, mask);

        setInputValue(maskedValue);
        onChange(maskedValue);

        // Position cursor correctly after backspace
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newPosition, newPosition);
          }
        }, 0);
      }
      return;
    }

    // Handle delete key
    if (e.keyCode === 46) {
      // Delete
      e.preventDefault();

      if (cursorPosition < inputValue.length) {
        const currentValue = inputValue;
        let newPosition = cursorPosition;

        // Skip over literal characters when deleting
        while (
          newPosition < currentValue.length &&
          mask &&
          !maskMap[mask[newPosition]]
        ) {
          newPosition++;
        }

        const newValue =
          currentValue.substring(0, cursorPosition) +
          currentValue.substring(newPosition + 1);
        const maskedValue = applyMask(newValue, mask);

        setInputValue(maskedValue);
        onChange(maskedValue);

        // Keep cursor at same position after delete
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
        }, 0);
      }
      return;
    }

    // Allow other control keys: tab, escape, enter, arrows, home, end
    if (
      [9, 27, 13, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      (e.ctrlKey && [65, 67, 86, 88, 90].indexOf(e.keyCode) !== -1)
    ) {
      return;
    }
  };

  return (
    <>
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      <div className="mb-3">
        {label && (
          <label htmlFor={id} className="form-label font-weight-bold">
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
        )}
        <input
          ref={inputRef}
          type="text"
          id={id}
          className={`form-control ${className}`}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          {...props}
        />
      </div>
    </>
  );
};

export default InputMask;
